// PostgreSQL built-in (pg_catalog) function names emitted by `pgsqlResolver`
// when the cursor is at a position that accepts a function call.
//
// PG implicitly searches `pg_catalog` for unqualified function lookup even
// when it is NOT in `search_path`, so these always surface as
// system-function candidates. The PG schema introspection deliberately
// excludes `pg_catalog` / `information_schema` from its routine fetch
// (`pgSchemaIntrospection.ts`), so this hardcoded curated list fills the
// gap without re-introspecting the system catalog at warm time.
//
// Selection criteria: functions documented in PostgreSQL's "Built-in
// Functions and Operators" chapter that are commonly used in user
// authoring (string / numeric / date-time / aggregate / JSON helpers).
// Operators (`||`, `->>`, etc.) are out of scope for completion — they
// surface via Monaco's keyword suggestions.

const PG_BUILTIN_FUNCTIONS: readonly string[] = [
  // Aggregate
  'array_agg', 'avg', 'bit_and', 'bit_or', 'bool_and', 'bool_or',
  'count', 'every', 'max', 'min', 'string_agg', 'sum', 'json_agg',
  'jsonb_agg', 'json_object_agg', 'jsonb_object_agg',
  // String
  'ascii', 'btrim', 'char_length', 'character_length', 'chr', 'concat',
  'concat_ws', 'format', 'initcap', 'left', 'length', 'lower', 'lpad',
  'ltrim', 'md5', 'octet_length', 'overlay', 'position', 'regexp_match',
  'regexp_matches', 'regexp_replace', 'regexp_split_to_array',
  'regexp_split_to_table', 'repeat', 'replace', 'reverse', 'right',
  'rpad', 'rtrim', 'split_part', 'starts_with', 'strpos', 'substr',
  'substring', 'translate', 'trim', 'upper',
  // Numeric
  'abs', 'cbrt', 'ceil', 'ceiling', 'degrees', 'div', 'exp', 'factorial',
  'floor', 'gcd', 'lcm', 'ln', 'log', 'log10', 'mod', 'pi', 'power',
  'radians', 'random', 'round', 'sign', 'sqrt', 'trunc', 'width_bucket',
  // Date/time
  'age', 'clock_timestamp', 'current_date', 'current_time',
  'current_timestamp', 'date_part', 'date_trunc', 'extract',
  'isfinite', 'justify_days', 'justify_hours', 'justify_interval',
  'localtime', 'localtimestamp', 'make_date', 'make_interval',
  'make_time', 'make_timestamp', 'make_timestamptz', 'now',
  'statement_timestamp', 'timeofday', 'transaction_timestamp',
  'to_timestamp',
  // Conditional / null
  'coalesce', 'greatest', 'least', 'nullif',
  // Type casting / formatting
  'to_char', 'to_date', 'to_number',
  // JSON / JSONB
  'array_to_json', 'json_array_elements', 'json_array_elements_text',
  'json_array_length', 'json_build_array', 'json_build_object',
  'json_each', 'json_each_text', 'json_extract_path',
  'json_extract_path_text', 'json_object', 'json_object_keys',
  'json_strip_nulls', 'json_to_record', 'json_to_recordset',
  'jsonb_array_elements', 'jsonb_array_elements_text',
  'jsonb_array_length', 'jsonb_build_array', 'jsonb_build_object',
  'jsonb_each', 'jsonb_each_text', 'jsonb_extract_path',
  'jsonb_extract_path_text', 'jsonb_object', 'jsonb_object_keys',
  'jsonb_pretty', 'jsonb_set', 'jsonb_strip_nulls', 'jsonb_to_record',
  'jsonb_to_recordset', 'row_to_json', 'to_json', 'to_jsonb',
  // System / introspection
  'current_database', 'current_schema', 'current_setting', 'current_user',
  'inet_client_addr', 'inet_client_port', 'inet_server_addr',
  'inet_server_port', 'pg_backend_pid', 'pg_typeof', 'session_user',
  'user', 'version',
  // Window
  'cume_dist', 'dense_rank', 'first_value', 'lag', 'last_value', 'lead',
  'nth_value', 'ntile', 'percent_rank', 'rank', 'row_number',
  // Generators
  'generate_series', 'generate_subscripts', 'unnest',
  // Full-text search — tsquery / tsvector builders + ranking. Pinned by
  // goals/autocomplete-coverage pgsql script.14 §3.
  'to_tsquery', 'plainto_tsquery', 'phraseto_tsquery', 'websearch_to_tsquery',
  'to_tsvector', 'ts_rank', 'ts_rank_cd', 'ts_headline',
];

/**
 * Set-returning functions usable as table sources in FROM. Subset of the
 * built-in list that PostgreSQL accepts as relation-shaped sources. Pinned
 * by goals/autocomplete-coverage pgsql script.21 §2.
 */
const PG_SET_RETURNING_FUNCTIONS: readonly string[] = [
  'generate_series', 'generate_subscripts', 'unnest',
  'regexp_split_to_table', 'regexp_matches',
  'jsonb_array_elements', 'jsonb_array_elements_text',
  'jsonb_each', 'jsonb_each_text', 'jsonb_object_keys',
  'json_array_elements', 'json_array_elements_text',
  'json_each', 'json_each_text', 'json_object_keys',
  'string_to_table',
];

export function getPgSetReturningFunctions(): readonly string[] {
  return PG_SET_RETURNING_FUNCTIONS;
}

export const PGSQL_BUILTIN_FUNCTIONS: ReadonlySet<string> = new Set(
  PG_BUILTIN_FUNCTIONS,
);

/** Lazy lookup; case-insensitive match against the curated set. */
export function isPgBuiltinFunction(name: string): boolean {
  return PGSQL_BUILTIN_FUNCTIONS.has(name.toLowerCase());
}

/**
 * The full curated list (lower-cased per PG convention). Resolver consumers
 * dedupe against the user's schema routines before emission.
 */
export function getPgBuiltinFunctions(): readonly string[] {
  return PG_BUILTIN_FUNCTIONS;
}
